// AnchoredOverlay — a floating overlay anchored at a pointer, positioned right.
//
// The component form of useOverlayAnchor, and the one most call sites want:
// every context menu in this app is rendered as `{menu && <div style={{
// position: 'fixed', left: menu.x, … }}>}` inside a much larger component, and
// a hook cannot be called from inside that conditional. Wrapping it in a
// component that only mounts when the menu is open puts the hook back at a top
// level — its own.
//
// What it fixes, in nine menus that each had its own copy of the same four
// lines (content browser ×2, Blueprint editor ×2, Event graph ×3, state-machine
// editor ×2, material graph ×2, transition-rule editor ×1):
//
//   - the CSS-zoom coordinate-space mix-up (uiZoomCoords.js has the
//     measurement: 62px of drift at the default UI scale);
//   - hand-estimated heights — `innerHeight - 300`, `- 80`, `- 120` — one guess
//     per menu, each wrong the moment an item was added;
//   - two menus (StateMachineEditor, TransitionRuleEditor) that clamped
//     NOTHING, so a right-click near an edge put the menu partly off-screen.
//
// It deliberately owns position only. Colour, border and z-index stay with each
// menu: these editors are visually distinct on purpose (the state-machine
// editor is green, the transition-rule editor amber) and centralising their
// chrome would be a redesign smuggled in behind a bug fix.

import React from 'react'
import { createPortal } from 'react-dom'
import { useOverlayAnchor } from '../lib/useOverlayAnchor'

/**
 * @param {number} x viewport pointer X (clientX / screenX as these menus name it)
 * @param {number} y viewport pointer Y
 * @param {number} [margin] gap kept from the viewport edges
 * @param {number} [submenuWidth] when set, `children` may be a function
 *   receiving `{ flip }` — true when a submenu of that width would leave the
 *   viewport if opened to the right.
 * @param {object|Function} [innerRef] receives the overlay node, alongside the
 *   hook's own measuring ref.
 * @param {object} [style] the overlay's own chrome; merged OVER the position,
 *   so a caller cannot accidentally clobber left/top by spreading a style with
 *   its own position keys — those are dropped below.
 */
export default function AnchoredOverlay({
  x, y, margin, submenuWidth, style, children, innerRef, portal, ...rest
}) {
  const { ref, props: anchor, flip } = useOverlayAnchor(x, y, { margin, submenuWidth })
  // The hook needs the node to measure it, and callers often need it too (an
  // outside-click check that must not fire on the overlay itself). One callback
  // ref feeds both, rather than making the caller choose which one wins.
  const setNode = (node) => {
    ref.current = node
    if (typeof innerRef === 'function') innerRef(node)
    else if (innerRef) innerRef.current = node
  }
  // Drop any position keys a caller's style still carries. Every one of these
  // menus previously wrote `position: 'fixed', left, top` in that same object,
  // so a half-finished conversion would silently put the old values back and
  // the fix would appear not to work.
  const { position, left, top, maxHeight: chromeMaxHeight, ...chrome } = style ?? {}
  // A caller's own maxHeight is a request for LESS, never for more: a menu that
  // wants to be at most 420px still must not exceed the window on a short
  // screen. Taking the minimum is what makes the hook's viewport cap
  // unconditional without overriding an intentional, tighter limit.
  const maxHeight = chromeMaxHeight === undefined
    ? anchor.style.maxHeight
    : Math.min(chromeMaxHeight, anchor.style.maxHeight ?? Infinity)

  const el = (
    <div {...anchor} ref={setNode} {...rest} style={{ ...anchor.style, ...chrome, maxHeight }}>
      {typeof children === 'function' ? children({ flip }) : children}
    </div>
  )
  // `portal` escapes an ancestor that would otherwise own this overlay: a
  // `position: fixed` box resolves against — and is clipped by — the nearest
  // ancestor carrying a transform, and every menu here carries the shared open
  // animation, which animates one. Measured on the asset submenu: a correctly
  // clamped `left: 740px` painted at 1385. Any overlay nested inside another
  // menu needs it.
  return portal ? createPortal(el, document.body) : el
}
